import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import UpgradeModal from "../components/UpgradeModal";

const SubscriptionContext = createContext(null);

const DEFAULT_PLANS = {
  pro_monthly: { name: "Pro Monthly", amount: 12, currency: "usd", days: 30 },
  pro_annual: { name: "Pro Annual", amount: 96, currency: "usd", days: 365 },
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [upgrade, setUpgrade] = useState({ open: false, reason: "" });

  const refresh = useCallback(async () => {
    if (!user) { setSub(null); return; }
    try {
      const res = await api.get("/subscription/me");
      setSub(res.data);
    } catch (e) { /* keep previous */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.get("/payments/config").then((r) => setPaypalClientId(r.data.paypal_client_id)).catch(() => {});
  }, []);

  const openUpgrade = useCallback((reason = "") => setUpgrade({ open: true, reason }), []);
  const closeUpgrade = useCallback(() => setUpgrade({ open: false, reason: "" }), []);

  const isPro = !!(sub && sub.is_pro);
  const plans = (sub && sub.plans) || DEFAULT_PLANS;

  return (
    <SubscriptionContext.Provider value={{ sub, isPro, plans, refresh, openUpgrade, closeUpgrade }}>
      {children}
      <UpgradeModal
        open={upgrade.open}
        reason={upgrade.reason}
        plans={plans}
        tiers={(sub && sub.tiers) || []}
        paypalClientId={paypalClientId}
        onClose={closeUpgrade}
        onSuccess={async () => { await refresh(); closeUpgrade(); }}
      />
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  return ctx || { sub: null, isPro: false, plans: DEFAULT_PLANS, refresh: () => {}, openUpgrade: () => {}, closeUpgrade: () => {} };
};
